// Mountain Time is the display default for every date (Pack C2). Must stay first.
import './utils/mountainTime';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { initTheme } from './components/ThemeToggle';
import axios from 'axios';
import { handleAuthExpiry } from './utils/authExpiry';
import { classifyError, recordSuppression } from './utils/errorNoise';

// ── Global 401 handling ──────────────────────────────────────────────────────
// Registered on the axios DEFAULT instance so it covers the 51 client files
// that import raw `axios`. Instances built with axios.create() (utils/api.js)
// do not inherit this and register their own — both call the same handler.
// Before this existed, an expired token produced a silently empty page.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try { handleAuthExpiry(error); } catch (e) { /* never mask the original error */ }
    return Promise.reject(error);
  }
);


// Applied before React mounts. Doing this in a component would paint light
// first and snap to dark on mount — a white flash on every load, which is
// precisely what someone enabling dark mode is trying to avoid.
initTheme();

// ─────────────────────────────────────────────────────────────────────────────
// Hotfix: suppress a known-benign third-party rejection.
//
// reCAPTCHA v3 (loaded site-wide via GoogleReCaptchaProvider in App.js) runs
// background bot-scoring on page load, which uses the Web Crypto API. Inside
// restricted in-app webviews — notably the Facebook in-app browser on Android —
// that crypto call throws:
//     OperationError: Non-recoverable error. Do not retry.
// Because it happens inside reCAPTCHA's own promise chain (not our code), it
// surfaces as an UNHANDLED promise rejection. It is harmless: the page renders
// normally, the visitor simply isn't bot-scored. But it was being reported to
// Sentry on every Facebook-app ad click that landed on the site, burning
// visibility on paid traffic.
//
// We match this ONE specific message narrowly so genuine errors still report.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Suppress the browser's own console noise for third-party rejections.
 *
 * The two hand-written reCAPTCHA regexes that lived here are gone. They were
 * the first two of five filters that accumulated one incident at a time; the
 * category behind all of them — URP running inside webviews and extensions it
 * does not control — is now handled once in utils/errorNoise.js. Two
 * implementations of one rule always drift (lesson 34), so this delegates.
 */
function isEnvironmentNoise(reason) {
  if (!reason) return false;
  const message = typeof reason === 'string' ? reason : (reason.message || '');
  const name = reason.name || '';
  return classifyError(message).noise || classifyError(`${name}: ${message}`).noise;
}

// Stop it from being treated as "unhandled" (prevents the browser console
// error). Runs before Sentry's own handler; preventDefault() doesn't stop
// other listeners, so Sentry is also filtered below.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (isEnvironmentNoise(event.reason)) {
      event.preventDefault();
    }
  });
}

// Error monitoring
try {
  const Sentry = require('@sentry/react');
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.NODE_ENV || 'development',
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.1,
      // Drop the benign reCAPTCHA/Web-Crypto rejection (see note above).
      // The third pattern is Sentry reporting on itself: baseclient throws a
      // SentryError whenever beforeSend returns null, and that throw was being
      // captured as a fresh event. The noise filter was manufacturing noise.
      // Cheap pre-filter for the highest-volume patterns so they never reach
      // beforeSend. The authoritative rules live in utils/errorNoise.js — this
      // list must stay a SUBSET of those, never diverge from them.
      ignoreErrors: [
        /Non-recoverable error\.?\s*Do not retry/i,
        /^\s*(?:error:\s*)?invalid origin\.?\s*$/i,
        /An event processor returned .?null.?, will not send event/i,
        /Blocked a frame with origin .* from accessing a cross-origin frame/i,
        /ResizeObserver loop/i,
        // Native bridge probes from in-app webviews (Facebook, Instagram,
        // Gmail). See utils/errorNoise.js — this list stays a SUBSET of that.
        /webkit\.messageHandlers|ReactNativeWebView|chrome\.webview|flutter_inappwebview/i
      ],
      /**
       * ONE classifier, not a growing list of one-off patterns.
       *
       * Five filters had accumulated here, each added in reaction to a single
       * report. The category behind all of them is the same: URP's code runs
       * inside environments it does not control — Facebook, Instagram, Gmail
       * and iMessage webviews inject scripts, patch DOM prototypes, block
       * storage APIs and abort requests on swipe-away. See utils/errorNoise.js
       * for the three rules that keep this from becoming a blanket suppressor.
       *
       * Suppressions are COUNTED rather than silently dropped, so an outbreak
       * remains visible in aggregate.
       */
      beforeSend(sentryEvent, hint) {
        const ex = sentryEvent && sentryEvent.exception &&
          sentryEvent.exception.values && sentryEvent.exception.values[0];
        const value = (ex && ex.value) || '';
        const type = (ex && ex.type) || '';
        const reason = hint && hint.originalException;
        const reasonMsg = (reason && (reason.message || String(reason))) || '';
        const frames = (ex && ex.stacktrace && ex.stacktrace.frames) || [];
        const culprit = frames.length ? (frames[frames.length - 1].filename || '') : '';

        for (const msg of [value, type + ': ' + value, reasonMsg]) {
          const verdict = classifyError(msg, culprit);
          if (verdict.noise) {
            recordSuppression(verdict.source);
            return null;
          }
        }
        return sentryEvent;
      },
    });
  }
} catch (e) {
  // Sentry not available — continue without monitoring
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
